<script>
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import { v4 as uuidv4 } from "uuid";
    import { onMount } from "svelte";
    import { BarLoader } from "svelte-loading-spinners";
    import { toast } from "@zerodevx/svelte-toast";
    import { localeState } from "@app/store/localeStore.svelte.js";
    import * as m from "@app/paraglide/messages.js";

    /* Firebase */
    import firebase from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import "firebase/functions";

    let keys = $state([]);
    let gettingKeys = $state();
    let loading = $state(false);
    let loadingMsg = $state(m.options_api_keys_working());
    let userId = firebase.auth().currentUser.uid;
    let dbRef = db.collection("users").doc(userId).collection("keys");

    onMount(() => {
        refreshKeys();
    });

    let locale = $derived(localeState.current);
    function refreshKeys() {
        gettingKeys = getKeys();
    }

    async function getKeys() {
        try {
            keys = [];
            loading = true;
            loadingMsg = m.options_api_keys_loading();
            let res = await firebase.functions().httpsCallable("v8GetApiKeys")();
            keys = res.data || [];
            return keys;
        } catch (error) {
            toast.push(m.options_api_keys_error_generic(), {
                classes: ["toast-danger"],
            });

            console.log(error);
            console.log(error.message);
            throw new Error(error.message);
        } finally {
            loading = false;
        }
    }

    async function createKey() {
        try {
            //Use Swal to get the name of the key
            let res = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                titleText: m.options_api_keys_new_title(),
                showCancelButton: true,
                confirmButtonText: m.options_api_keys_create_button(),
                input: "text",
                inputLabel: m.options_api_keys_name_label(),
                inputPlaceholder: m.options_api_keys_name_placeholder(),
            });

            if (res.isConfirmed && res.value) {
                let keyData = {
                    id: uuidv4(),
                    name: res.value,
                    key: null,
                    role: "admin",
                };

                loading = true;
                loadingMsg = m.options_api_keys_creating();
                await firebase.functions().httpsCallable("v8CreateApiKey")(
                    keyData,
                );
                refreshKeys();
            }
        } catch (err) {
            toast.push(m.options_api_keys_error_generic(), {
                classes: ["toast-danger"],
            });
            console.log(err);
            console.log(err.message);
            refreshKeys();
        } finally {
            loading = false;
        }
    }

    async function removeKey(id) {
        try {
            let res = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.destructive,
                titleText: m.options_api_keys_delete_title(),
                text: m.options_api_keys_delete_confirm(),
                showCancelButton: true,
                confirmButtonText: m.options_api_keys_delete_button(),
            });

            if (res.isConfirmed) {
                loading = true;
                loadingMsg = m.options_api_keys_deleting();
                await firebase.functions().httpsCallable("v8DeleteApiKey")(id);
                refreshKeys();
            }
        } catch (err) {
            toast.push(m.options_api_keys_error_generic(), {
                classes: ["toast-danger"],
            });

            console.log(err);
            console.log(err.message);
            refreshKeys();
        } finally {
            loading = false;
        }
    }

    async function editKey(id) {
        try {
            //Use Swal to get the name of the key
            let res = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                titleText: m.options_api_keys_edit_title(),
                showCancelButton: true,
                confirmButtonText: m.options_api_keys_update_button(),
                input: "text",
                inputLabel: m.options_api_keys_name_label(),
                inputValue : keys.find(key => key.id === id).name,
                inputPlaceholder: m.options_api_keys_name_placeholder(),
            });

            if (res.isConfirmed && res.value) {
                let keyData = {
                    id: id,
                    name: res.value,
                    role: "admin",
                };

                loading = true;
                loadingMsg = m.options_api_keys_updating();
                await firebase.functions().httpsCallable('v8UpdateApiKey')( keyData );
                refreshKeys();
            }
        } catch (err) {
            toast.push(m.options_api_keys_error_generic(), {
                classes: ["toast-danger"],
            });
            console.log(err);
            console.log(err.message);
            refreshKeys();
        } finally {
            loading = false;
        }
    }
</script>

{#key locale}
{#await gettingKeys}
    <BarLoader size="250" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
    <p>{m.options_api_keys_loading()}</p>
{:then}
    {#if loading}
        <BarLoader size="250" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
        <p>{loadingMsg}</p>
    {:else}
    <div class="table-responsive">
        <table class="table" aria-label={m.options_api_keys_table_label()}>
            <thead>
                <tr>
                    <th>{m.options_api_keys_table_name()}</th>
                    <th>{m.options_api_keys_table_key()}</th>
                    <th>{m.options_api_keys_table_created_on()}</th>
                    <th>{m.options_api_keys_table_action()}</th>
                </tr>
            </thead>
            <tbody>
                {#each keys as key (key.id)}
                    <tr>
                        <td>{key.name}</td>
                        <td class="font-plexMono"
                            >{key.hidden ? "*****************************" : key.key}
                            {#if !key.hidden}
                            <button
                                aria-label={m.options_api_keys_copy_label()}
                                title={m.options_api_keys_copy_label()}
                                class="btn btn-secondary float-end"
                                type="button"
                                disabled={loading}
                                onclick={() => {
                                    navigator.clipboard.writeText(key.key);
                                    toast.push(m.options_api_keys_copied(), {
                                        classes: ["toast-success"],
                                    });
                                    key.hidden = true;
                                }}
                            >
                                <i class="bi bi-copy"></i>
                            </button>
                            {:else}
                            <button
                                aria-label={m.options_api_keys_show_label()}
                                class="btn btn-primary float-end"
                                type="button"
                                disabled={loading}
                                onclick={() => {
                                    key.hidden = false;
                                }}
                            >
                                <i class="bi bi-eye"></i>
                            </button>
                            {/if}
                        </td>
                        <td>{key.createdOn.substring(0, 24)}</td>
                        <td>
                            <div class="btn-group" role="group">
                                <button
                                class="btn btn-secondary"
                                type="button"
                                disabled={loading}
                                onclick={() => editKey(key.id)}
                                aria-label={m.options_api_keys_edit_label()}
                            >
                                <i class="bi bi-pen"></i>
                            </button>
                            <button
                                class="btn btn-outline-danger"
                                type="button"
                                disabled={loading}
                                onclick={() => removeKey(key.id)}
                                aria-label={m.options_api_keys_delete_label()}
                            >
                                <i class="bi bi-trash"></i>
                            </button>
                            </div>
                            
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
    {/if}
{:catch error}
    <p class="text-danger">{m.options_api_keys_error_generic()}</p>
    <p>{m.options_api_keys_contact_support()}</p>
{/await}
{/key}
<div class="btn-group" role="group">
    <button id="createApiKeyButton" class="btn btn-primary" type="button" disabled={loading} onclick={() => createKey()} aria-label={m.options_api_keys_create_label()}>
        {m.options_api_keys_new_button()}
    </button>
</div>
